import * as MultiChoiceService from "@/components/common/multi-choice/multi-choice-service";
import _ from "lodash";
import { getOptions } from "@/components/common/multi-choice/multi-choice-service";

export const SiteFacingMultiChoiceEditable = {
  props: {
    question: {
      type: Object,
      default: () => ({})
    },
    lang: {
      type: String,
      default: ""
    },
    translationKeyInitial: {
      type: String,
      default: ""
    },
    isFieldDisabled: {
      type: Boolean,
    },
    mode: {
      type: Number,
      default: 0
    },
    isRTLlang: {
      type: Boolean,
      default: false
    },
    canScore: {
      type: Boolean,
      default: false
    },
    triggerValidate: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      selectedAnswers: [],
      scores: {},
      otherOption: {},
      otherOptionComment: null,
      otherCommentMaxLength: 1000,
      noneOptionId: null
    };
  },
  mounted() {
    this.resetQuestion();
    this.selectedAnswers = MultiChoiceService.getOptionIds(this.question);

    if (this.otherOptionComment && this.otherOptionComment.value) {
      this.emitAnswerChanged();
    }
  },
  watch: {
    triggerValidate() {
      if (this.isEditing) {
        this.validate();
      }
    }
  },
  computed: {
    isEditing() {
      return this.mode === 1;
    },
    isDisabled() {
      if (!this.isEditing) {
        return true;
      }

      if (this.isEditing && this.question.isSkipped === true) {
        return true;
      }

      if (this.isEditing && this.noneOptionId && this.selectedAnswers.includes(this.noneOptionId)) {
        return true;
      }

      return false;
    },
    options() {
      return MultiChoiceService.getOptions(this.question);
    },
    filteredOptions() {
      return this.isOtherOptionEnabled ? this.options.filter(o => o.id !== this.otherOptionId) : this.options;
    },
    otherCommentLength() {
      return this.otherOptionComment.value ? this.otherOptionComment.value.length : 0;
    },
    optionIds() {
      return MultiChoiceService.getOptionIds(this.question);
    },
    isOtherOptionEnabled() {
      return MultiChoiceService.isOtherOptionEnabled(this.question) && this.otherOptionId;
    },
    isOtherCommentRequired() {
      const isSelected = MultiChoiceService.isOtherOptionSelected(this.question);
      const hasComment = MultiChoiceService.isOtherOptionCommentEnabled(this.question);

      return this.isOtherOptionEnabled && isSelected && hasComment;
    },
    isOtherSelected() {
      return MultiChoiceService.isOtherOptionSelected(this.question);
    },
    otherOptionId() {
      return MultiChoiceService.getOtherOptionId(this.question);
    },
    otherOptionLabel() {
      return MultiChoiceService.getOtherOptionLabel(this.question);
    },
    otherOptionPlaceholder() {
      return MultiChoiceService.getOtherOptionPlaceholder(this.question);
    },
    score() {
      return this.selectedAnswers.reduce((acc, id) => acc += this.scores[id] || 0, 0);
    }
  },
  methods: {
    getOtherOption() {
      if (this.otherOptionId && this.isOtherOptionEnabled) {
        const sequence = this.options.length + 1;

        return {
          body: "",
          colorCoding: {
            color: "#fff",
            warningMessage: "Please enter a comment"
          },
          enabled: true,
          excludeScore: false,
          formattedBody: "",
          id: this.otherOptionId,
          isAdvancedScoring: false,
          priority: 0,
          score: this.question.otherScore || 0,
          sendNotification: this.question.isNotifyToEmail,
          sequence: sequence
        };
      }
    },
    clearQuestion() {
      if (!this.isFieldDisabled) {
        this.resetQuestion(true);
      }
    },
    resetQuestion(isCleared = false) {
      if (isCleared) {
        this.question.answer = undefined;
        this.question.optionsIds = [];
        this.question.computedScore = 0;
        this.question.actualScore = 0;
        this.question.dataChecks = [];
      }

      this.question.hasFieldError = isCleared;
      this.question.hasFieldWarning = isCleared;
      this.question.options = MultiChoiceService.getOptions(this.question);

      if (this.isEditing) {
        this.selectedAnswers = MultiChoiceService.isMulti(this.question) ? [] : [null];
        this.otherOption = this.getOtherOption();
        this.otherOptionComment = MultiChoiceService.getOtherAnswerComment(this.question);
      }

      if (this.isEditing && this.otherOption) {
        const otherOptionInQuestion = this.question.options.find(option => option.id === this.otherOption.id);

        if (otherOptionInQuestion) {
          this.otherOption = otherOptionInQuestion;
        } else {
          this.question.options.push(this.otherOption);
        }

        this.question.optionsIds = isCleared ? [] : MultiChoiceService.getOptionIds(this.question);
      }

      this.clearError();
      this.calculateScores();
      this.$emit("getQuestionScores");
    },
    calculateScores() {
      this.scores = {};

      this.question.options.forEach(o => {
        this.scores[o.id] = o.score || 0;
      });

      if (this.isOtherOptionEnabled) {
        this.scores[this.otherOptionId] = this.question.otherScore || 0;
      }
    },
    validate(setWarning = false) {
      this.clearError();

      const canBeSkipped = this.question.isSkipped || this.question.isSkippable;

      let errorMessage;

      if (!canBeSkipped && (this.selectedAnswers.length === 0 || !this.selectedAnswers[0])) {
        errorMessage = "This field is required.";
      } else if (this.isOtherCommentRequired && !this.otherOptionComment.value) {
        errorMessage = "Please enter a comment.";
      }

      if (errorMessage) {
        this.setFieldErrorText(errorMessage, setWarning);
      }
    },
    clearError() {
      this.setFieldErrorText("", true);
    },
    setFieldErrorText(message, setWarning = false) {
      this.fieldErrorText = message;
      this.question.hasFieldError = !!message;
      this.question.fieldErrorText = message;

      if (setWarning) {
        this.fieldWarningText = message;
      }
    },
    onKeydownOtherComment(event) {
      if (event.key === ":") {
        event.preventDefault();
      }
    },
    debouncedUpdateAnswer: _.debounce(function() {
      this.updateQuestion();
    }, 300),
    onInputOtherComment(event) {
      const newValue = event.target.value;
      this.otherOptionComment.value = newValue.replace(/:/g, "");
      this.debouncedUpdateAnswer();
    },
    onAnswerChange(value) {
      const optionIds = MultiChoiceService.toArray(value);
      const noneOption = this.question.options.find(o => (o.formattedBody || "").toLowerCase() === "none");
      this.noneOptionId = noneOption ? noneOption.id : null;

      if (optionIds.includes(this.noneOptionId)) {
        this.selectedAnswers = [this.noneOptionId];
      } else {
        this.selectedAnswers = optionIds;
      }

      this.question.optionsIds = this.selectedAnswers;
      this.updateQuestion();
    },
    updateQuestion() {
      this.validate(true);
      this.emitAnswerChanged();
    },
    emitAnswerChanged() {
      if (this.options) {
        // Important: always sort options ids by sequence and other option id as the last element
        const sortedOptions = this.filteredOptions.sort((a, b) => a.sequence - b.sequence).filter(opt => this.selectedAnswers.includes(opt.id));
        let otherAnswerBody;

        if (this.isOtherSelected) {
          if (this.isOtherCommentRequired && this.otherOptionComment.value) {
            otherAnswerBody = `${this.otherOptionLabel}: ${this.otherOptionComment.value}`;
          } else {
            otherAnswerBody = `${this.otherOptionLabel}`;
          }

          this.otherOption.body = otherAnswerBody;
          this.otherOption.formattedBody = otherAnswerBody;
        }

        // Important: always sort options ids by sequence and other option id as the last element
        if (this.question.optionsIds && this.question.optionsIds instanceof Array) {
          this.question.optionsIds = sortedOptions.map(o => o.id).concat(otherAnswerBody ? [this.question.otherOptionId] : []);
        }
        this.question.optionId = sortedOptions.map(o => o.id).concat(otherAnswerBody ? [this.question.otherOptionId] : []).join(",");
        this.question.answer = sortedOptions.map(o => (o ? o.body : null)).filter(b => b !== null).concat(otherAnswerBody ? [otherAnswerBody] : []).join(", ");
        this.question.computedScore = this.score;
        this.question.actualScore = this.score;

        this.$emit(`answerChanged`, {
          questionId: this.question.id,
          newAns: {
            ...this.question,
            _otherOptionComment: this.otherOptionComment.value
          }
        });
      }
    }
  }
};
